import React, {
  Suspense,
  lazy,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { _get } from "../services/apiClient";
import { useSearch } from "../SearchContext";
import { useTranslation } from "react-i18next";
import ServiceUnavailable from "../Components/ServiceUnavailable";
import { useRestaurantDetails } from "../context/RestaurantDetailsContext";
import { getAppConfig } from "../utilis/appConfig";

const MenuItem = lazy(() => import("../Components/MenuItem"));

const PAGE_LIMIT = 24;
// Height of the sticky header (title/search row + category tabs row).
const SCROLL_OFFSET = 150;

/**
 * Menu — customer-friendly menu browsing.
 *
 * Differences from Product.js (which is intentionally left untouched):
 *  - All categories are shown on top as a horizontal carousel.
 *  - Every product is loaded through on-scroll pagination (no category in the URL).
 *  - Products are grouped by category into sections; as the customer scrolls,
 *    the matching category chip is highlighted automatically and scrolled into
 *    view inside the carousel.
 *  - Tapping a chip jumps to that category's section, loading more pages first
 *    if that category hasn't been fetched yet.
 */
function Menu({ handleClick, cart, setCart, restaurantStatus }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const { restaurantDetails } = useRestaurantDetails();
  const { defaultImage } = getAppConfig();

  const { searchData, handleSearchChange, filter: foodFilter, setFilter } =
    useSearch();
  const searchTerm = (searchData || "").trim();
  const isSearching = searchTerm.length > 0;

  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [isFetching, setIsFetching] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState(null);
  const [pendingScrollCat, setPendingScrollCat] = useState(null);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [showCategoryMenu, setShowCategoryMenu] = useState(false);
  const [showFilterMenu, setShowFilterMenu] = useState(false);
  const [isCategoryNavigating, setIsCategoryNavigating] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(Boolean(searchData));

  const sentinelRef = useRef(null);
  const searchInputRef = useRef(null);
  const chipRefs = useRef({});
  const activeCategoryRef = useRef(null);
  // While a manual chip navigation is in flight we suppress the scroll spy so
  // the customer doesn't see the highlight flick through intermediate
  // categories during the smooth scroll.
  const manualNavRef = useRef(false);
  const manualNavCleanupRef = useRef(null);
  const navSpinnerTimerRef = useRef(null);
  // Monotonic id so a slow in-flight request can't overwrite fresher results.
  const reqIdRef = useRef(0);

  const isRTL = i18n.dir() === "rtl";

  // ---- QR (dine-in) detection: strictly from the URL ----
  const queryParams = new URLSearchParams(location.search);
  const tableId = queryParams.get("tableId");
  const restaurantId = queryParams.get("restaurantId");
  const isQrEntry = Boolean(tableId && restaurantId);

  const getLocalizedText = useCallback(
    (field) => {
      if (!field) return "";
      if (typeof field === "string") return field;
      if (typeof field === "object") return field[i18n.language] || field.en || "";
      return "";
    },
    [i18n.language]
  );

  const normalizeId = (val) => {
    if (!val) return null;
    if (typeof val === "string") return val;
    if (typeof val === "object" && val.$oid) return val.$oid;
    if (typeof val === "object" && val._id) return normalizeId(val._id);
    if (typeof val === "object" && val.id) return val.id;
    return String(val);
  };

  // ---------------------------------------------------------------------------
  // Data fetching
  // ---------------------------------------------------------------------------
  const fetchCategories = useCallback(async () => {
    try {
      const res = await _get(`/categories`);
      setCategories(res.data || []);
    } catch (error) {
      console.error("Error fetching categories:", error);
    }
  }, []);

  const fetchPage = useCallback(
    async (pageToLoad) => {
      const reqId = ++reqIdRef.current;
      try {
        setIsFetching(true);

        const params = new URLSearchParams();
        params.set("page", pageToLoad);
        params.set("limit", PAGE_LIMIT);

        if (searchTerm) params.set("search", searchTerm);
        if (foodFilter === "veg" || foodFilter === "nonveg") {
          params.set("foodType", foodFilter);
        }
        if (isQrEntry) {
          params.set("mode", "dinein");
          params.set("tableId", tableId);
          params.set("restaurantId", restaurantId);
        }

        // Small delay on subsequent pages so the loader is visible.
        if (pageToLoad > 1) {
          await new Promise((resolve) => setTimeout(resolve, 400));
        }

        const res = await _get(`/products/menu?${params.toString()}`);

        // A newer request started while this one was in flight — drop it.
        if (reqId !== reqIdRef.current) return;

        setProducts((prev) =>
          pageToLoad === 1
            ? res.data.products || []
            : [...prev, ...(res.data.products || [])]
        );
        setTotalPages(res.data.totalPages || 1);
      } catch (error) {
        console.error("Error fetching products:", error);
      } finally {
        if (reqId === reqIdRef.current) {
          setIsFetching(false);
          setInitialLoading(false);
        }
      }
    },
    [searchTerm, foodFilter, isQrEntry, tableId, restaurantId]
  );

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  // Keep the search field expanded once there's a term to show/clear.
  useEffect(() => {
    if (searchData) setIsSearchOpen(true);
  }, [searchData]);

  const openSearch = () => {
    setIsSearchOpen(true);
    setTimeout(() => searchInputRef.current?.focus(), 0);
  };

  const closeSearch = () => {
    if (searchData) return;
    setIsSearchOpen(false);
  };

  // Reset + reload whenever the search term or the veg/non-veg filter changes.
  useEffect(() => {
    setProducts([]);
    setPage(1);
    setTotalPages(1);
    setPendingScrollCat(null);
    setInitialLoading(true);
    fetchPage(1);
  }, [fetchPage]);

  // Jump back to the top when the customer starts/edits a search or flips the
  // veg filter. Runs after the DOM updates (and disables scroll anchoring on
  // the list) so a bottom scroll position can't leave the results off-screen.
  useLayoutEffect(() => {
    window.scrollTo(0, 0);
  }, [searchTerm, foodFilter]);

  // Load the next page whenever `page` advances.
  useEffect(() => {
    if (page > 1) fetchPage(page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  const canLoadMore = page < totalPages && !isFetching;

  // ---------------------------------------------------------------------------
  // Infinite scroll
  // ---------------------------------------------------------------------------
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && page < totalPages && !isFetching) {
          setPage((prev) => prev + 1);
        }
      },
      { rootMargin: "600px 0px" }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [page, totalPages, isFetching]);

  // ---------------------------------------------------------------------------
  // Group loaded products by category, in the order categories are listed.
  // ---------------------------------------------------------------------------
  const groupedSections = useMemo(() => {
    if (isSearching) return [];

    const buckets = new Map();
    categories.forEach((cat, index) => {
      buckets.set(normalizeId(cat._id), {
        idx: index,
        catId: normalizeId(cat._id),
        name: cat.name,
        items: [],
      });
    });

    const extras = new Map();
    products.forEach((product) => {
      const catId = normalizeId(product.category);
      if (buckets.has(catId)) {
        buckets.get(catId).items.push(product);
      } else {
        if (!extras.has(catId)) {
          extras.set(catId, {
            idx: Number.MAX_SAFE_INTEGER,
            catId,
            name: product.categoryName || { en: "Other" },
            items: [],
          });
        }
        extras.get(catId).items.push(product);
      }
    });

    return [...buckets.values(), ...extras.values()]
      .filter((bucket) => bucket.items.length > 0)
      .sort((a, b) => a.idx - b.idx);
  }, [categories, products, isSearching]);

  const sectionKey = groupedSections.map((s) => s.catId).join("|");

  // ---------------------------------------------------------------------------
  // Scroll spy — highlight the category currently in view.
  // ---------------------------------------------------------------------------
  const scrollChipIntoView = useCallback((catId) => {
    const chip = chipRefs.current[catId];
    if (chip) {
      chip.scrollIntoView({
        behavior: "smooth",
        inline: "center",
        block: "nearest",
      });
    }
  }, []);

  // Lock the scroll spy until the page has settled after a programmatic scroll.
  const beginManualNav = useCallback((catId) => {
    // Tear down any nav already in flight before arming a new one.
    if (manualNavCleanupRef.current) manualNavCleanupRef.current();

    manualNavRef.current = true;
    activeCategoryRef.current = catId;

    let settleTimer = null;
    const release = () => {
      window.removeEventListener("scroll", onScroll);
      clearTimeout(settleTimer);
      clearTimeout(maxTimer);
      manualNavCleanupRef.current = null;
      manualNavRef.current = false;
    };
    const onScroll = () => {
      clearTimeout(settleTimer);
      settleTimer = setTimeout(release, 250);
    };
    const maxTimer = setTimeout(release, 1500);

    window.addEventListener("scroll", onScroll, { passive: true });
    // Fallback in case the target is already in place and no scroll fires.
    settleTimer = setTimeout(release, 400);
    manualNavCleanupRef.current = release;
  }, []);

  // Jump to a category section instantly — the jump itself is masked behind
  // the nav spinner overlay, so there's no smooth-scroll motion to see.
  // Returns false if the section isn't rendered yet.
  const scrollToSection = useCallback(
    (catId) => {
      const el = document.getElementById(`cat-section-${catId}`);
      if (!el) return false;
      beginManualNav(catId);
      const y =
        el.getBoundingClientRect().top + window.pageYOffset - SCROLL_OFFSET;
      window.scrollTo({ top: y, behavior: "auto" });
      return true;
    },
    [beginManualNav]
  );

  // Keeps the nav spinner up for a minimum stretch so it doesn't flash, then
  // hides it once the (now-instant) jump has actually landed.
  const finishCategoryNav = useCallback(() => {
    if (navSpinnerTimerRef.current) clearTimeout(navSpinnerTimerRef.current);
    navSpinnerTimerRef.current = setTimeout(() => {
      setIsCategoryNavigating(false);
      navSpinnerTimerRef.current = null;
    }, 1000);
  }, []);

  useEffect(
    () => () => {
      if (manualNavCleanupRef.current) manualNavCleanupRef.current();
      if (navSpinnerTimerRef.current) clearTimeout(navSpinnerTimerRef.current);
    },
    []
  );

  useEffect(() => {
    if (isSearching || groupedSections.length === 0) return;

    const sections = Array.from(
      document.querySelectorAll("[data-cat-section]")
    );
    if (sections.length === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (manualNavRef.current) return;

        const visible = entries
          .filter((entry) => entry.isIntersecting)
          .sort(
            (a, b) => a.boundingClientRect.top - b.boundingClientRect.top
          );
        if (visible.length === 0) return;

        const catId = visible[0].target.getAttribute("data-cat-id");
        if (catId && catId !== activeCategoryRef.current) {
          activeCategoryRef.current = catId;
          setActiveCategory(catId);
          scrollChipIntoView(catId);
        }
      },
      { rootMargin: "-45% 0px -50% 0px", threshold: 0 }
    );

    sections.forEach((section) => observer.observe(section));
    return () => observer.disconnect();
  }, [sectionKey, isSearching, groupedSections.length, scrollChipIntoView]);

  // ---------------------------------------------------------------------------
  // Chip tap — jump to a category, loading more pages first if needed.
  // ---------------------------------------------------------------------------
  const goToCategory = (catId) => {
    setActiveCategory(catId);
    // Suppress the spy straight away so the tap doesn't flicker other chips.
    manualNavRef.current = true;
    activeCategoryRef.current = catId;
    scrollChipIntoView(catId);

    // Cover the jump with a spinner instead of letting the user see it.
    if (navSpinnerTimerRef.current) clearTimeout(navSpinnerTimerRef.current);
    setIsCategoryNavigating(true);

    if (scrollToSection(catId)) {
      finishCategoryNav();
    } else {
      // Category not fetched yet — keep loading pages, then scroll to it.
      setPendingScrollCat(catId);
    }
  };

  const handleCategoryMenuSelect = (catId) => {
    setShowCategoryMenu(false);
    goToCategory(catId);
  };

  // Deep-link support: arriving with ?category=<id> (e.g. tapping a category
  // card on the home screen) jumps straight to that section once the first
  // page of products has loaded, then strips the param from the URL.
  const deepLinkCategoryRef = useRef(true);
  useEffect(() => {
    if (!deepLinkCategoryRef.current || initialLoading) return;
    deepLinkCategoryRef.current = false;

    const catId = new URLSearchParams(location.search).get("category");
    if (!catId) return;

    goToCategory(catId);

    const params = new URLSearchParams(location.search);
    params.delete("category");
    navigate(
      {
        pathname: location.pathname,
        search: params.toString() ? `?${params.toString()}` : "",
      },
      { replace: true }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialLoading]);

  useEffect(() => {
    if (!pendingScrollCat) return;

    if (scrollToSection(pendingScrollCat)) {
      setPendingScrollCat(null);
      finishCategoryNav();
    } else if (page < totalPages && !isFetching) {
      setPage((prev) => prev + 1);
    } else if (!isFetching) {
      // Nothing more to load — the category has no products.
      setPendingScrollCat(null);
      manualNavRef.current = false;
      finishCategoryNav();
    }
  }, [
    pendingScrollCat,
    products,
    page,
    totalPages,
    isFetching,
    scrollToSection,
    finishCategoryNav,
  ]);

  // ---------------------------------------------------------------------------
  // Scroll-to-top button
  // ---------------------------------------------------------------------------
  useEffect(() => {
    const onScroll = () => setShowScrollTop(window.scrollY > 500);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleFoodFilter = (type) => {
    setFilter(foodFilter === type ? "" : type);
  };

  if (
    restaurantDetails &&
    restaurantDetails.amcStatus &&
    restaurantDetails.amcStatus !== "active"
  ) {
    return <ServiceUnavailable />;
  }

  const renderProductGrid = (items) => (
    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
      <Suspense
        fallback={
          <div className="px-3 py-2 text-sm text-gray-400">
            <i>Loading items…</i>
          </div>
        }
      >
        {items.map((detail, index) => (
          <MenuItem
            key={`${detail._id || index}-${i18n.language}`}
            MenuItem={detail}
            handleClick={handleClick}
            products={items}
            cart={cart}
            setCart={setCart}
          />
        ))}
      </Suspense>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-b from-brand-50 to-brand-50">
      {/* Header: back + title/search + category & filter icons, all in one row */}
      <div className="sticky top-14 sm:top-16 z-30 bg-white border-b border-gray-200 shadow-sm">
        <div className="flex items-center gap-1.5 px-3 sm:px-4 py-2">
          <button
            type="button"
            onClick={() => navigate({ pathname: "/", search: location.search })}
            className="text-gray-400 hover:text-brand-600 transition-colors flex-shrink-0 w-7 h-7 flex items-center justify-center"
            aria-label="Back"
          >
            <svg className="w-4.5 h-4.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2.5}
                d="M15 19l-7-7 7-7"
              />
            </svg>
          </button>

          {isSearchOpen ? (
            <div className="relative group flex-1">
              <div className="absolute inset-y-0 left-0 pl-2.5 flex items-center pointer-events-none">
                <svg
                  className="h-4 w-4 text-brand-400 group-focus-within:text-brand-500 transition-colors duration-200"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2.5}
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
              </div>
              <input
                ref={searchInputRef}
                type="search"
                className="w-full pl-8 pr-3 py-1.5 text-xs sm:text-sm bg-brand-50/50 border border-brand-200/60 rounded-full focus:bg-white focus:ring-2 focus:ring-brand-400/20 focus:border-brand-400 outline-none transition-all placeholder-gray-400 font-medium"
                onChange={handleSearchChange}
                onBlur={closeSearch}
                placeholder={t("searchPlaceholder")}
                value={searchData}
                autoFocus
              />
            </div>
          ) : (
            <>
              <span className="text-sm font-semibold text-gray-800 flex-1 truncate">
                {t("ourMenu") || "Our Menu"}
              </span>
              <button
                type="button"
                onClick={openSearch}
                className="flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-full text-gray-500 hover:text-brand-600 hover:bg-brand-50 transition-colors"
                aria-label={t("searchPlaceholder") || "Search"}
              >
                <svg className="w-4.5 h-4.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2.5}
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
              </button>
            </>
          )}

          {!isSearching && categories.length > 0 && (
            <button
              type="button"
              onClick={() => setShowCategoryMenu(true)}
              className="flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-full text-gray-500 hover:text-brand-600 hover:bg-brand-50 transition-colors"
              aria-label={t("allCategories") || "All Categories"}
            >
              <svg className="w-4.5 h-4.5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M3 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clipRule="evenodd" />
              </svg>
            </button>
          )}

          {/* Veg / Non-veg filter, collapsed into one icon */}
          <div className="relative flex-shrink-0">
            <button
              type="button"
              onClick={() => setShowFilterMenu((v) => !v)}
              className={`w-8 h-8 flex items-center justify-center rounded-full border transition-colors ${
                foodFilter
                  ? "border-brand-400 bg-brand-50 text-brand-600"
                  : "border-transparent text-gray-500 hover:bg-gray-50"
              }`}
              aria-label="Filter"
            >
              <svg className="w-4.5 h-4.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M3 4.5h18M6 9.5h12M10 14.5h4M11 19.5h2"
                />
              </svg>
            </button>

            {showFilterMenu && (
              <>
                <div
                  className="fixed inset-0 z-30"
                  onClick={() => setShowFilterMenu(false)}
                />
                <div
                  className={`absolute top-full mt-2 z-40 bg-white rounded-xl shadow-xl border border-gray-100 p-1.5 w-36 ${
                    isRTL ? "left-0" : "right-0"
                  }`}
                >
                  <button
                    onClick={() => {
                      handleFoodFilter("veg");
                    }}
                    className={`w-full flex items-center gap-2 px-2.5 py-2 rounded-lg text-xs font-medium transition ${
                      foodFilter === "veg"
                        ? "bg-green-100 text-green-700"
                        : "text-gray-600 hover:bg-gray-50"
                    }`}
                  >
                    🥬 {t("vegFilter")}
                  </button>
                  <button
                    onClick={() => {
                      handleFoodFilter("nonveg");
                    }}
                    className={`w-full flex items-center gap-2 px-2.5 py-2 rounded-lg text-xs font-medium transition mt-1 ${
                      foodFilter === "nonveg"
                        ? "bg-red-100 text-red-700"
                        : "text-gray-600 hover:bg-gray-50"
                    }`}
                  >
                    🍗 {t("nonVegFilter")}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>

        {!isSearching && categories.length > 0 && (
          <div className="overflow-x-auto scrollbar-hide px-3 sm:px-4">
            <div className="flex gap-5 sm:gap-6">
              {categories.map((cat) => {
                const catId = normalizeId(cat._id);
                const isActive = activeCategory === catId;
                return (
                  <button
                    key={catId}
                    ref={(node) => {
                      if (node) chipRefs.current[catId] = node;
                    }}
                    onClick={() => goToCategory(catId)}
                    className={`flex-shrink-0 pb-2 pt-0.5 text-xs sm:text-sm whitespace-nowrap border-b-2 transition-colors ${
                      isActive
                        ? "border-brand-500 text-brand-600 font-semibold"
                        : "border-transparent text-gray-500 font-medium hover:text-brand-500"
                    }`}
                  >
                    {getLocalizedText(cat.name)}
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {restaurantStatus === "offline" && (
        <div
          className="alert alert-danger text-center fw-bold py-2 mt-2"
          role="alert"
          style={{ fontSize: "1.25rem", color: "#dc3545" }}
        >
          {t("restaurantOffline")}
        </div>
      )}

      <div
        className="container position-relative"
        style={{ overflowAnchor: "none" }}
      >
        {initialLoading ? (
          <div className="flex items-center justify-center h-[70vh]">
            <div className="relative">
              <div className="w-14 h-14 border-4 border-brand-200 rounded-full"></div>
              <div className="absolute top-0 left-0 w-14 h-14 border-4 border-brand-500 border-t-transparent rounded-full animate-spin"></div>
            </div>
          </div>
        ) : isSearching ? (
          // -------- Search results (flat list) --------
          <>
            {products.length > 0 ? (
              <div className="pt-3">{renderProductGrid(products)}</div>
            ) : (
              !isFetching && (
                <h5 className="text-danger text-center mt-4">
                  Sorry, no items matched your search.
                </h5>
              )
            )}
          </>
        ) : (
          // -------- Grouped browse --------
          <>
            {groupedSections.map((section) => (
              <div
                key={section.catId}
                id={`cat-section-${section.catId}`}
                data-cat-section
                data-cat-id={section.catId}
              >
                <div className="py-3 mb-3 md:py-4 md:mb-4 px-3 md:px-4 bg-white">
                  <div className="flex items-center gap-3">
                    <div className="h-8 w-1 bg-gradient-to-b from-brand-500 to-brand-700 rounded-full"></div>
                    <h6 className="text-sm md:text-base font-semibold text-gray-900 mb-0">
                      {getLocalizedText(section.name)}{" "}
                      <span className="text-gray-400 font-normal">
                        ({section.items.length})
                      </span>
                    </h6>
                  </div>
                </div>
                <div className="px-3 md:px-4 mb-3">
                  {renderProductGrid(section.items)}
                </div>
              </div>
            ))}

            {groupedSections.length === 0 && !isFetching && (
              <h5 className="text-danger text-center mt-4">
                {t("filteredItemDesc") || "No items available."}
              </h5>
            )}
          </>
        )}

        {/* Infinite-scroll sentinel + loader */}
        <div ref={sentinelRef} />

        {isFetching && (
          <div className="flex items-center justify-center py-4 mb-20">
            <div className="relative">
              <div className="w-12 h-12 border-4 border-brand-200 rounded-full"></div>
              <div className="absolute top-0 left-0 w-12 h-12 border-4 border-brand-500 border-t-transparent rounded-full animate-spin"></div>
            </div>
          </div>
        )}

        {!isFetching && !canLoadMore && products.length > 0 && (
          <div className="text-center py-4 mb-20 text-gray-500 text-sm">
            <p>✨ You've reached the end</p>
          </div>
        )}
      </div>

      {showScrollTop && (
        <button
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          className={`fixed bottom-20 sm:bottom-6 z-[70] w-12 h-12 sm:w-14 sm:h-14 bg-gradient-to-br from-brand-500 via-brand-600 to-brand-700 text-white rounded-full shadow-2xl transform hover:scale-110 transition-all duration-300 flex items-center justify-center ${
            isRTL ? "right-4 sm:right-6" : "left-4 sm:left-6"
          }`}
          aria-label="Scroll to top"
        >
          <svg
            className="w-5 h-5 sm:w-6 sm:h-6"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2.5}
              d="M5 10l7-7m0 0l7 7m-7-7v18"
            />
          </svg>
        </button>
      )}

      {isCategoryNavigating && (
        <div className="fixed inset-0 z-[90] flex items-center justify-center bg-white/80 backdrop-blur-sm">
          <div className="relative">
            <div className="w-14 h-14 border-4 border-brand-200 rounded-full"></div>
            <div className="absolute top-0 left-0 w-14 h-14 border-4 border-brand-500 border-t-transparent rounded-full animate-spin"></div>
          </div>
        </div>
      )}

      {showCategoryMenu && (
        <div
          className="fixed inset-0 z-[80] flex items-end sm:items-center justify-center bg-black/50 backdrop-blur-sm"
          onClick={() => setShowCategoryMenu(false)}
        >
          <div
            className="w-full sm:max-w-lg max-h-[75vh] bg-white rounded-t-3xl sm:rounded-3xl shadow-2xl overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-4 py-3 border-b border-brand-100 bg-gradient-to-r from-brand-50 to-brand-50">
              <h3 className="text-sm font-bold text-gray-900">
                {t("selectCategory") || "Select Category"}
              </h3>
              <button
                type="button"
                onClick={() => setShowCategoryMenu(false)}
                className="text-gray-400 hover:text-brand-600 transition-colors"
                aria-label="Close"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            <div className="overflow-y-auto px-4 py-4">
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
                {categories.map((cat) => {
                  const catId = normalizeId(cat._id);
                  const isActive = activeCategory === catId;
                  return (
                    <button
                      key={catId}
                      type="button"
                      onClick={() => handleCategoryMenuSelect(catId)}
                      className={`flex flex-col items-center gap-1.5 p-2 rounded-2xl border transition-all ${
                        isActive
                          ? "border-brand-500 bg-brand-50"
                          : "border-gray-200 bg-white hover:border-brand-300"
                      }`}
                    >
                      <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-xl overflow-hidden bg-gradient-to-br from-brand-50 to-brand-50 flex-shrink-0">
                        <img
                          src={
                            cat.images?.[0]
                              ? `${process.env.REACT_APP_IMAGE_BASE_URL}${cat.images[0]}`
                              : defaultImage
                                ? `${process.env.REACT_APP_IMAGE_BASE_URL}${defaultImage}`
                                : "/img/finglider_logo.jpg"
                          }
                          alt={getLocalizedText(cat.name)}
                          loading="lazy"
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = defaultImage
                              ? `${process.env.REACT_APP_IMAGE_BASE_URL}${defaultImage}`
                              : "/img/finglider_logo.jpg";
                          }}
                        />
                      </div>
                      <span className="text-[11px] sm:text-xs font-medium text-gray-800 text-center leading-tight line-clamp-2">
                        {getLocalizedText(cat.name)}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Menu;
